import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  MousePointerClick, PhoneCall, ClipboardCheck, FileSignature, Megaphone,
  HandCoins, CheckCircle2, Trophy, CreditCard, BadgeEuro, ArrowRight, ShieldCheck, Zap, Eye
} from "lucide-react";
import { MarketingNav, Footer } from "@/components/MarketingNav";

const STEPS = [
  { icon: MousePointerClick, title: "Segnali", desc: "Inserisci i dati essenziali del potenziale venditore e dell'immobile." },
  { icon: PhoneCall, title: "Contatto", desc: "Il team Pistone Parla Chiaro contatta il proprietario." },
  { icon: ClipboardCheck, title: "Valutazione", desc: "Prima analisi dell'immobile e verifica della reale intenzione di vendita." },
  { icon: FileSignature, title: "Incarico", desc: "Se ci sono le condizioni, viene acquisito l'incarico di vendita." },
  { icon: Megaphone, title: "Gestione vendita", desc: "Marketing, pubblicità, appuntamenti, visite, documentazione, trattative." },
  { icon: HandCoins, title: "Proposte", desc: "Le proposte ricevute vengono gestite professionalmente." },
  { icon: CheckCircle2, title: "Accettazione", desc: "Il proprietario accetta una proposta e la pratica prosegue." },
  { icon: Trophy, title: "Affare concluso", desc: "Al verificarsi delle condizioni previste lo stato si aggiorna." },
  { icon: CreditCard, title: "Dati di pagamento", desc: "Confermi i dati necessari al pagamento e alla gestione fiscale." },
  { icon: BadgeEuro, title: "Merito fidelizzazione", desc: "La piattaforma mostra il merito fidelizzazione maturato e poi pagato." },
];

export default function Home() {
  return (
    <div className="min-h-screen bg-[#0A1128] text-white">
      <MarketingNav />

      {/* HERO */}
      <section className="relative pt-16 min-h-[92vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1757924461488-ef9ad0670978?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBsdXh1cnklMjBhcGFydG1lbnQlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODY0NTA1Nzh8MA&ixlib=rb-4.1.0&q=85"
            alt="Immobile di pregio" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-[#0A1128]/85" />
          <div className="absolute inset-0 grain" />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-20 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div className="lg:col-span-7"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-4 py-1.5 text-xs font-medium text-[#E6C965]">
              <Zap className="h-3.5 w-3.5" /> Piattaforma PropTech italiana
            </span>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05]">
              Conosci qualcuno che <span className="text-[#D4AF37]">vuole vendere casa?</span>
            </h1>
            <p className="mt-5 text-lg text-white/70 max-w-xl leading-relaxed">
              Segnalalo. Al resto pensiamo noi. Se la segnalazione si trasforma in un affare
              concluso, ricevi il merito fidelizzazione previsto.
            </p>
            <p className="mt-6 font-display text-xl text-white">Tu segnali. <span className="text-[#D4AF37]">Noi pensiamo al resto.</span></p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link to="/segnala" data-testid="hero-segnala-btn"
                className="btn-gold rounded-full px-8 py-4 text-base font-semibold inline-flex items-center justify-center gap-2">
                Segnala un immobile <ArrowRight className="h-5 w-5" />
              </Link>
              <Link to="/come-funziona" data-testid="hero-come-funziona-btn"
                className="rounded-full border border-white/25 px-8 py-4 text-base font-medium text-white hover:bg-white/10 transition-colors inline-flex items-center justify-center">
                Come funziona
              </Link>
            </div>
          </motion.div>
          <motion.div className="lg:col-span-5"
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}>
            <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md p-6 space-y-4">
              {[
                { icon: ShieldCheck, t: "Trasparente", d: "Segui ogni fase dalla tua dashboard." },
                { icon: Zap, t: "Velocissimo", d: "Fai una segnalazione in pochi tocchi." },
                { icon: Eye, t: "Tracciabile", d: "Ogni pratica ha un codice, es. PPC-000127." },
              ].map((f, i) => (
                <div key={i} className="flex items-start gap-4 rounded-xl p-4 bg-[#0A1128]/40 border border-white/5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#D4AF37]/15 text-[#D4AF37]">
                    <f.icon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="font-display font-medium text-white">{f.t}</p>
                    <p className="text-sm text-white/60">{f.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* PROCESS TIMELINE */}
      <section className="py-24 bg-[#0A1128] relative">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-2xl">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold">Il processo, in chiaro.</h2>
            <p className="mt-3 text-white/60">Dieci passi semplici. Tu ti occupi solo del primo.</p>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
            {STEPS.map((s, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.4, delay: (i % 5) * 0.06 }}
                className="card-hover rounded-xl border border-white/10 bg-white/5 p-5">
                <div className="flex items-center justify-between">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#D4AF37]/15 text-[#D4AF37]">
                    <s.icon className="h-5 w-5" />
                  </span>
                  <span className="font-display text-2xl font-semibold text-white/15">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="mt-4 font-display font-medium text-white">{s.title}</h3>
                <p className="mt-1.5 text-sm text-white/55 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SPLIT CTA */}
      <section className="py-24 bg-[#1E293B]">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-2xl overflow-hidden border border-white/10">
            <img src="https://images.unsplash.com/photo-1653762379954-8943c787e78b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxwZXJzb24lMjBzbWlsaW5nJTIwbG9va2luZyUyMGF0JTIwc21hcnRwaG9uZXxlbnwwfHx8fDE3ODY0NTA1Nzh8MA&ixlib=rb-4.1.0&q=85"
              alt="Persona che usa lo smartphone" className="w-full h-[420px] object-cover" />
          </div>
          <div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold">Tu conosci le persone. <span className="text-[#D4AF37]">Noi sappiamo vendere gli immobili.</span></h2>
            <p className="mt-5 text-white/70 leading-relaxed">
              Non devi diventare agente immobiliare. Non devi fare visite, negoziare o occuparti
              della documentazione. Devi semplicemente metterci in contatto con una possibile
              opportunità immobiliare.
            </p>
            <ul className="mt-6 space-y-3">
              {["Trasforma le tue relazioni in opportunità", "Segui tutto dalla tua dashboard", "Il riconoscimento dipende solo dalle tue segnalazioni personali"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-white/80">
                  <CheckCircle2 className="h-5 w-5 text-[#D4AF37]" /> {t}
                </li>
              ))}
            </ul>
            <Link to="/registrati" data-testid="split-registrati-btn"
              className="btn-gold mt-8 inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-semibold">
              Inizia ora <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 bg-[#0A1128] text-center">
        <div className="mx-auto max-w-3xl px-5">
          <h2 className="font-display text-3xl sm:text-5xl font-semibold">Tu segnali. Noi pensiamo al resto.</h2>
          <p className="mt-4 text-white/60">E quando una segnalazione produce un risultato economico secondo le condizioni previste, guadagni anche tu.</p>
          <Link to="/segnala" data-testid="final-segnala-btn"
            className="btn-gold mt-8 inline-flex items-center gap-2 rounded-full px-8 py-4 font-semibold">
            Segnala un immobile <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
